import { z } from 'zod'

export interface ChannelDefinition<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TChannel extends string | ((params: any) => string),
  TEvents extends Record<string, { schema: z.ZodTypeAny }>,
> {
  channel: TChannel
  events: TEvents
}

// Type helpers that extract types from the channel definition
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type EventName<T extends ChannelDefinition<any, any>> = keyof T['events']

export type PayloadOf<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  T extends ChannelDefinition<any, any>,
  E extends EventName<T>,
> = T['events'][E] extends { schema: infer S extends z.ZodTypeAny } ? z.infer<S> : never

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ChannelParamsOf<T extends ChannelDefinition<any, any>> = T['channel'] extends (params: infer P) => string
  ? P
  : never

// Channel factory function
export function defineChannel<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const TChannel extends string | ((params: any) => string),
  const TEvents extends Record<string, { schema: z.ZodTypeAny }>,
>(def: ChannelDefinition<TChannel, TEvents>): ChannelDefinition<TChannel, TEvents> {
  return def
}

// Helper functions
export function getEventSchema<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  T extends ChannelDefinition<any, any>,
  E extends EventName<T>,
>(channelDef: T, event: E): z.ZodTypeAny {
  const eventDef = channelDef.events[event as string]
  if (!eventDef) {
    throw new Error(`Event ${String(event)} not found in channel`)
  }
  return eventDef.schema
}

export function resolveChannel<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  T extends ChannelDefinition<any, any>,
>(channelDef: T, params?: ChannelParamsOf<T>): string {
  if (typeof channelDef.channel === 'string') {
    return channelDef.channel
  }
  return channelDef.channel(params!)
}
